import { useCallback, useEffect, useMemo } from "react";

import { Consumable, ConsumableKey, ConsumableLevel } from "../api/useConsumables";
import { useStateContext } from "../contexts/ContextProvider";
import { useLocalStorageState } from "./useLocalStorageState";

/**
 * Mise en veille de l'affichage d'une alerte de solde (cf. CONTEXT.md).
 *
 * Ce qu'on ferme est un bandeau, jamais une alerte : l'état reste vrai, la
 * notification e-mail est déjà partie, la pastille du menu profil reste
 * allumée. C'est un accusé de réception — « j'ai vu, je traite » —, pas une
 * résolution.
 *
 * 24 h, parce qu'une veille se compte en heures de traitement et non en
 * semaines : les établissements concernés sont, depuis le filtre d'engagement,
 * réellement bloqués.
 */
const SNOOZE_MS = 24 * 60 * 60 * 1000;

type SnoozeMap = Record<string, number>;

/**
 * L'utilisateur est dans la clé parce que les postes de réception sont
 * PARTAGÉS entre équipes : sans lui, le réceptionniste de nuit hériterait de
 * l'accusé de réception de celui du jour.
 *
 * L'établissement y est parce qu'un utilisateur multi-établissements en bascule
 * sans recharger la page — `useLocalStorageState` relit alors la nouvelle clé,
 * et lui seul sait le faire sans provoquer un rendu supplémentaire au montage.
 */
function storageKey(userId: number, facilityId: number): string {
    return `tf:consumable-snooze:${userId}:${facilityId}`;
}

/** Le niveau est dans l'entrée : c'est ce qui fait remonter une escalade. */
function entryKey(key: ConsumableKey, level: ConsumableLevel): string {
    return `${key}:${level}`;
}

export function useConsumableSnooze(consumables: Consumable[]) {
    const { user, currentFacility } = useStateContext();
    const facilityId = currentFacility?.id ?? user?.facility_id ?? 0;
    const key = useMemo(() => storageKey(user?.id ?? 0, facilityId), [user?.id, facilityId]);

    const [snoozed, setSnoozed] = useLocalStorageState<SnoozeMap>(key, {});

    /**
     * Les deux nettoyages qui gardent la veille honnête.
     *
     * Le premier — purger les entrées d'un consommable revenu à `ok` — est le
     * pendant front de la suppression de ligne d'état côté scan. Sans lui, un
     * hôtel qui recharge le matin et retombe à zéro l'après-midi resterait
     * aveugle jusqu'au lendemain : le nouvel épisode hériterait de la veille du
     * précédent, alors que c'en est un autre.
     *
     * Le second écarte les entrées expirées ou illisibles, pour que la clé ne
     * grossisse pas et qu'une valeur écrite par une version antérieure ne soit
     * jamais interprétée comme une veille.
     *
     * L'escalade `low → exhausted`, elle, ne demande aucun code : le niveau est
     * dans la clé d'entrée, donc une veille sur `low` ne couvre pas `exhausted`.
     */
    useEffect(() => {
        const now = Date.now();
        const recovered = new Set(
            consumables.filter((item) => item.level === 'ok').map((item) => item.key),
        );

        setSnoozed((current) => {
            const next: SnoozeMap = Object.fromEntries(
                Object.entries(current).filter(([entry, expiresAt]) => typeof expiresAt === 'number'
                    && expiresAt > now
                    && !recovered.has(entry.split(':')[0] as ConsumableKey)),
            );

            // Même contenu : renvoyer l'objet courant évite un rendu de plus.
            return Object.keys(next).length === Object.keys(current).length ? current : next;
        });
    }, [consumables, setSnoozed]);

    const isSnoozed = useCallback(
        (consumable: ConsumableKey, level: ConsumableLevel) => {
            const expiresAt = snoozed[entryKey(consumable, level)];

            return expiresAt !== undefined && expiresAt > Date.now();
        },
        [snoozed],
    );

    const snooze = useCallback(
        (consumable: ConsumableKey, level: ConsumableLevel) => {
            setSnoozed((current) => ({
                ...current,
                [entryKey(consumable, level)]: Date.now() + SNOOZE_MS,
            }));
        },
        [setSnoozed],
    );

    return { isSnoozed, snooze };
}
